import { Fragment, useState } from 'react';
import Button from 'src/components/buttons/button';
import NumberedList from 'src/components/numberedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}lower-your-cholesterol/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li>
        <div className="navigator__contents__number">1.</div>
        <div className="navigator__contents__content-text">
          <a href="#1">Boost your willpower</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">2.</div>
        <div className="navigator__contents__content-text">
          <a href="#2">The BHF recipe finder</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">3.</div>
        <div className="navigator__contents__content-text">
          <a href="#3">Sign up for our free magazine</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">4.</div>
        <div className="navigator__contents__content-text">
          <a href="#4">Download the BHF cholesterol booklet</a>
        </div>
      </li>
    </ul>
  </section>
);

const getPage = () => (
  <div className="navigator">
    <section className="navigator__content">
      <h1 className="navigator__title">Week 12: How we can support you long-term</h1>
      <p>
        The BHF has several ways to help support you on your journey to lower cholesterol. Eating well, exercising more
        and keeping to healthy habits can help reduce your risk of heart and circulatory diseases in the future by
        lowering your cholesterol and preventing other risk factors.
      </p>
      <div className="visible-xs">{getMenu()}</div>

      <h2>Boost your willpower</h2>
      <section className="navigator__section-half">
        <p>
          {`Making these changes takes willpower. But you can't rely on willpower forever. You need to make healthy
          choices daily habits, so you don't even know you're making them.`}
        </p>
        <NumberedList
          items={[
            <Fragment key="1">
              <strong> Develop a routine.</strong>{` It makes it easier to build healthy habits into your day and planning
              ahead means you won't find yourself ordering unhealthy takeaways.`}
            </Fragment>,
            <Fragment key="2">
              <strong>Build up slowly.</strong> Trying to change everything in your life in one week is too much. Focus
              on small changes and keep building on your successes.
            </Fragment>,
            <Fragment key="3">
              <strong>Get support.</strong> Tell your friends and family about the changes you want to make and why.
              Share your healthy changes with them, like going for a walk or cooking a nutritious meal.{' '}
            </Fragment>,
          ]}
        />
      </section>
      <img alt="3 men running in a race" className="navigator__image-half" src={`${PROGRAM_URL}W12B01.jpg`} />
      <a
        href="https://www.bhf.org.uk/heart-matters-magazine/wellbeing/willpower-top-10-tips"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Tips to boost willpower"
        />
      </a>

      <h2>The BHF recipe finder</h2>
      <section className="navigator__section-half">
        <p>
          {`A balanced diet is key to keeping your blood pressure at a healthy level. No matter what you're looking for,
          the BHF recipe finder can help you filter through our huge recipe collection to find hearty, healthy meals
          you'll love.`}
        </p>
        <a
          href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder"
          rel="noreferrer"
          target="_blank"
        >
          <Button
            icon="open-right"
            iconLeftAligned={false}
            text="Search for a recipe"
          />
        </a>
      </section>
      <img alt="Salmon and rice" className="navigator__image-half" src={`${PROGRAM_URL}W12B02.jpg`} />

      <h2>Sign up for our free magazine</h2>
      <section className="navigator__section-half">
        <p>
          {`Heart Matters magazine is the British Heart Foundation's award-winning FREE magazine for anyone with a heart
          or circulatory condition or at risk of getting one. It includes the latest research, healthy recipes and
          nutrition tips, and shares the journeys of other patients.`}
        </p>
        <p>
          High cholesterol is a real risk factor for heart and circulatory diseases, so our resources feature lots of
          ways to manage your condition and live a longer, healthier life.
        </p>
        <p>Check out the online magazine to get a taste of what will come through your letterbox every month.</p>
      </section>
      <img alt="Heart Matters the rough cut" className="navigator__image-half" src={`${PROGRAM_URL}W12B03.jpg`} />
      <a
        href="https://www.bhf.org.uk/heart-health/how-we-can-help/heart-matters"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Get a free subscription"
        />
      </a>

      <h2>Download the BHF cholesterol booklet</h2>
      <p>
        Our cholesterol booklet is filled with practical advice and ideas of changes you can make to help lower your
        cholesterol. It includes how to eat better, move more and how to form healthy habits.{' '}
      </p>
      <a
        href="https://www.bhf.org.uk/publications/heart-conditions/reducing-your-blood-cholesterol"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Download the booklet"
        />
      </a>

      <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
    </section>
  </div>
);

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="Woman with headset" className="navigator__footer__image" src={`${PROGRAM_URL}cta-helpline.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">Call our Heart Helpline</div>
        <div className="navigator__footer__description">
          Our Heart Helpline has Cardiac Nurses available to answer your questions. Call us on 0300 330 3311 or email{' '}
          <a
            className="navigator__footer__link"
            href="mailto:hearthelpline@bhf.org.uk"
          >
            hearthelpline@bhf.org.uk
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Lower your cholesterol | HealthUnlocked',
};

export default obj;
